import React, { FC, Fragment, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    Order,
    OrderStatus,
    OrderType,
    OperationButtonsConfig,
    InvoicingState,
    ProductionValueReport,
} from 'models/order';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ForwardIcon from '@mui/icons-material/Forward';
import CreateWarrantyOrderDialog from 'components/orders/order-details/create-warranty-order-dialog.component';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DeclineByLabDialog from 'components/orders/order-details/decline-dialog-component';
import ProductionFinishedDialog from 'components/orders/order-details/production-finished-dialog-component';
import { markOrderProductionFinishedTemplate, markOrderProductionNotFinishedTemplate } from 'apollo/templates/order';
import CopyOrderDialog from 'components/orders/order-details/copy-order-dialog.component';
import { useOngoingOrderDialog } from 'hooks/useOngoingOrderDialog';
import OngoingOrderDialogContainer from 'components/orders/shared/ongoing-order-dialog/ongoing-order-dialog.container';
import { restorationActionRowsByActionId } from 'utils/restoration-price-rows';
import { computeProductionValueSteps } from 'components/shared/production-value-report/production-value-report.utils';

type SupplierOperationSectionProps = {
    order: Order;
    showOperationButtons: OperationButtonsConfig | undefined;
    onHandleDeliveryNoteCreation: () => void;
};

const SupplierOperationSection: FC<SupplierOperationSectionProps> = ({
    order,
    showOperationButtons,
    onHandleDeliveryNoteCreation,
}: SupplierOperationSectionProps) => {
    const { t: translate } = useTranslation(['orders', 'workflow']);
    const navigate = useNavigate();
    const currentUser = currentUserVar();
    const [showProductionFinishedButton, setShowProductionFinishedButton] = useState(false);
    const [isProductionFinished, setIsProductionFinished] = useState(false);
    const [showCreateWarrantyOrderButton, setShowCreateWarrantyOrderButton] = useState(false);
    const [warrantyOrderDialogOpen, setWarrantyOrderDialogOpen] = useState(false);
    const [declineLabDialog, setDeclineLabDialog] = useState(false);
    const [productionFinishedDialog, setProductionFinishedDialog] = useState(false);
    const [copyOrderDialogOpen, setCopyOrderDialogOpen] = useState(false);
    const [productionPlanningData, setProductionPlanningData] = useState<{
        orderId: string;
        productionValueReports: ProductionValueReport[];
    }>();

    const { open, orders, operationType, close, continueOperation, checkOngoingOrder } = useOngoingOrderDialog({
        onOpenWarrantyDialog: () => setWarrantyOrderDialogOpen(true),
        onOpenCopyDialog: () => setCopyOrderDialogOpen(true),
    });

    useEffect(() => {
        setShowProductionFinishedButton(
            order.status.toUpperCase() !== OrderStatus.SENT_BY_CUSTOMER &&
                !order.isImportedOrder &&
                !(order.invoice && order.invoicingState === InvoicingState.INVOICED),
        );
        setIsProductionFinished(order.isProductionFinished);
        setShowCreateWarrantyOrderButton(
            order.isProductionFinished || order.invoicingState !== InvoicingState.NOT_READY,
        );
    }, [order]);

    const [markOrderProductionFinished] = useMutation(markOrderProductionFinishedTemplate, {
        onCompleted: () => {
            onHandleDeliveryNoteCreation();
        },
    });

    const [markOrderProductionNotFinished] = useMutation(markOrderProductionNotFinishedTemplate, {});

    const handleCloseDialog = () => {
        setWarrantyOrderDialogOpen(false);
        setCopyOrderDialogOpen(false);
        setDeclineLabDialog(false);
        setProductionFinishedDialog(false);
    };

    const handleProductionValueReportChange = (data: {
        orderId: string;
        productionValueReports: ProductionValueReport[];
    }) => {
        setProductionPlanningData(data);
    };

    const handleProductionDialogChange = async () => {
        const closeDialog = () => {
            handleCloseDialog();
        };

        if (isProductionFinished) {
            await markOrderProductionNotFinished({
                variables: {
                    orderId: order?._id,
                },
            });
        } else {
            let productionValueReports;
            if (productionPlanningData) {
                const actionRowMap = restorationActionRowsByActionId(order?.restorations);
                let hasMissingPriceSpec = false;
                productionValueReports =
                    productionPlanningData.productionValueReports
                        ?.flatMap((productionValueReport) =>
                            productionValueReport.actions.map((action) => {
                                const actionRow = actionRowMap.get(action.actionId.toString());

                                if (!actionRow) {
                                    hasMissingPriceSpec = true;
                                    return null;
                                }

                                const steps = computeProductionValueSteps(
                                    action,
                                    actionRow,
                                    productionValueReport.steps,
                                );

                                return {
                                    actionId: action.actionId,
                                    steps,
                                };
                            }),
                        )
                        .filter(Boolean) || [];

                if (hasMissingPriceSpec) {
                    console.log('Missing price specification for one or more actions');
                    return;
                }
            }
            await markOrderProductionFinished({
                variables: {
                    orderId: order?._id,
                    productionValueReports: productionValueReports,
                },
            });
        }
        closeDialog();
    };

    const redirectToLabOrderModify = (orderId: string) => {
        navigate(`/orders/modify-lab/${orderId}`);
    };

    const redirectToDraftActionOrder = (orderId: string) => {
        navigate(`/orders/create-action/draft/${orderId}`);
    };

    const redirectToOrderCreation = (order: Order) => {
        // for lab created order
        // TODO: add supplier order logic when merged
        if (order.isCreatedByLab) {
            return redirectToLabOrderModify(order._id);
        }
        if (order.type === OrderType.SIMPLE_TYPE) {
            return navigate(`/orders/modify-simple/${order._id}`);
        }
        // for action based order
        return redirectToDraftActionOrder(order._id);
    };

    // DD-3391: the elevated external tier marks production finished and sends to
    // the clinic on the orders shared with them, which is what the backend now
    // permits. Copying an order, raising a warranty order and declining are
    // lifecycle actions rather than production work, and stay with lab staff.
    const isInternalLabUser = !permissions.isExternalTechnician(currentUser.role);
    const canRunProduction = isInternalLabUser || permissions.isElevatedExternalUser(currentUser.role);

    return (
        <Fragment>
            {canRunProduction ? (
                <Fragment>
                    {order.status.toUpperCase() !== OrderStatus.SENT_BY_CUSTOMER && !isProductionFinished ? (
                        <Button
                            onClick={() => setProductionFinishedDialog(true)}
                            sx={{ marginLeft: 2 }}
                            startIcon={<CheckCircleIcon />}
                            data-test="technician-operation-section.finish-production-btn"
                        >
                            {translate('orders:FINISH_PRODUCTION')}
                        </Button>
                    ) : undefined}
                    {showProductionFinishedButton && isProductionFinished ? (
                        <Button
                            onClick={() => setProductionFinishedDialog(true)}
                            sx={{ marginLeft: 2 }}
                            startIcon={<CancelIcon />}
                            data-test="technician-operation-section.unfinish-production-btn"
                        >
                            {translate('orders:PRODUCTION_NOT_FINISHED')}
                        </Button>
                    ) : undefined}
                    {isInternalLabUser && order.type === OrderType.ACTION_BASED ? (
                        <Button
                            onClick={() => checkOngoingOrder(order, 'COPY_ORDER')}
                            sx={{ marginLeft: 2 }}
                            startIcon={<ContentCopyIcon />}
                            data-test="technician-operation-section.copy-order-btn"
                        >
                            {translate('orders:COPY_ORDER')}
                        </Button>
                    ) : undefined}
                    {/* DD-3484 Marketplace: subcontract this order (or part of it) to a connected partner lab. */}
                    {isInternalLabUser && !order.isDraft && !order.rejectStatus ? (
                        <Button
                            onClick={() => navigate(`/orders/subcontract/${order._id}`)}
                            sx={{ marginLeft: 2 }}
                            startIcon={<ForwardIcon />}
                            data-test="technician-operation-section.subcontract-order-btn"
                        >
                            {translate('orders:SUBCONTRACT_ORDER')}
                        </Button>
                    ) : undefined}
                    {isInternalLabUser && showCreateWarrantyOrderButton ? (
                        <Button
                            onClick={() => checkOngoingOrder(order, 'WARRANTY_ORDER')}
                            sx={{ marginLeft: 2 }}
                            startIcon={<VerifiedUserIcon />}
                            data-test="technician-operation-section.create-warranty-order-btn"
                        >
                            {translate('orders:CREATE_WARRANTY_ORDER')}
                        </Button>
                    ) : undefined}
                    {isInternalLabUser && !order.isImportedOrder && showOperationButtons?.declineByLab ? (
                        <Button
                            sx={{ marginLeft: 2 }}
                            startIcon={<CancelIcon />}
                            onClick={() => setDeclineLabDialog(true)}
                            data-test="technician-operation-section.decline-by-lab-btn"
                        >
                            {translate('orders:DECLINE_THE_ORDER')}
                        </Button>
                    ) : undefined}
                </Fragment>
            ) : undefined}

            <OngoingOrderDialogContainer
                open={open}
                orders={orders}
                operationType={operationType}
                onClose={close}
                onHandleOperation={continueOperation}
            />
            <CreateWarrantyOrderDialog
                open={warrantyOrderDialogOpen}
                onDialogClose={handleCloseDialog}
                onCreateWarrantyOrder={redirectToOrderCreation}
                orderDbId={order._id}
                orderId={order.orderId}
                isCreatedByLab={order.isCreatedByLab || false}
                isDentist={false}
            />
            <DeclineByLabDialog open={declineLabDialog} onClose={handleCloseDialog} order={order} />
            <ProductionFinishedDialog
                order={order}
                isProductionFinished={isProductionFinished}
                open={productionFinishedDialog}
                onClose={handleCloseDialog}
                productionFinishedDialogChange={handleProductionDialogChange}
                productionValueReportChange={handleProductionValueReportChange}
            />
            <CopyOrderDialog
                open={copyOrderDialogOpen}
                onDialogClose={handleCloseDialog}
                onCreateCopyOrder={redirectToOrderCreation}
                orderDbId={order._id}
                orderId={order.orderId}
            />
        </Fragment>
    );
};
export default SupplierOperationSection;
